import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { package_list } from "../../../core/data/json/packagelist";
import DatePicker from "react-datepicker";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { initialSettings } from "../../../core/common/selectoption/selectoption";
import Select from "react-select";
import {
  createPlan,
  deletePlan,
  fetchPlans,
  Plan,
  updatePlan,
} from "../../../api/useplan";
import { toast } from "react-toastify";
import {
  fetchPlanWidgetCounts,
  PlanWidgetCounts,
} from "../../../api/usermanager";
import { highlightMatch } from "../../../core/common/highlightmatch";

const Packages = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [widgetCounts, setWidgetCounts] = useState<PlanWidgetCounts | null>(
    null
  );
  const [tableData, setTableData] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [formData, setFormData] = useState<Plan>({
    plan_name: "",
    plan_type: "",
    plan_position: "",
    no_admin: "",
    no_user: "",
    plan_price: 0,
    plan_currency: "",
    discount_type: "",
    discount_percentage: "",
    trail_days: 0,
    status: "",
  });
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "Plan Name",
    "Plan Type",
    "Price",
    "No. of Admins",
    "No. of Users",
    "Currency",
    "Discount Type",
    "Discount %",
    "Trial Days",
    "Created Date",
    "Status",
    "Action",
  ]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("recently_added");
  const [filteredTableData, setFilteredTableData] = useState<any[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<
    [Date, Date] | null
  >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeRangeLabel, setActiveRangeLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const handleToggleColumn = (columnName: string) => {
    setVisibleColumns((prev) =>
      prev.includes(columnName)
        ? prev.filter((col) => col !== columnName)
        : [...prev, columnName]
    );
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);

      try {
        // ✅ Fetch Plan List
        const plansData = await fetchPlans();
        setPlans(plansData);
        const formattedPlans = plansData.map((item, idx) => ({
          key: item.plan_id || idx,
          Plan_Name: item.plan_name,
          Plan_Type: item.plan_type,
          Price: item.plan_price?.toString() || "",
          Plan_Position: item.plan_position,
  No_Admin: item.no_admin,
  No_User: item.no_user,
  Currency: item.plan_currency,
  Discount_Type: item.discount_type,
  Discount_Percentage: item.discount_percentage,
  Trial_Days: item.trail_days,
          Status: item.status,
          plan_id: item.plan_id,
          Created_Date: item.created_at || "",
        }));
        setTableData(formattedPlans);

        // ✅ Fetch Widget Counts
        const widgetData = await fetchPlanWidgetCounts();
        setWidgetCounts(widgetData);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const handleCreatePlan = async () => {
    try {
      await createPlan(formData); // 🔥 Use formData instead of hardcoded values
      toast.success("Plan created successfully");
      refreshData();
      // Optional: Reset form after submit
      setFormData({
        plan_name: "",
        plan_type: "",
        plan_position: "",
        no_admin: "",
        no_user: "",
        plan_price: 0,
        plan_currency: "",
        discount_type: "",
        discount_percentage: "",
        trail_days: 0,
        status: "",
      });
    } catch (error) {
      console.error("Failed to create plan:", error);
    }
  };

  const handleUpdatePlan = async (id: string) => {
    try {
      const payload = {
        ...formData,
        plan_price: parseFloat(formData.plan_price.toString()) || 0,
        trail_days: parseInt(formData.trail_days.toString()) || 0,
      };

      await updatePlan(id, payload); // Replace with your actual update API function
      toast.success("Plan updated successfully");
      fetchPlans(); // Refresh your plan list
      refreshData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeletePlan = async (planId: string) => {
    try {
      await deletePlan(planId);
      toast.success("Plan deleted successfully");
      refreshData();
    } catch (error) {
      console.error("Failed to delete plan:", error);
    }
  };

  const refreshData = () => {
    fetchPlans().then((data) => {
      setPlans(data);

      const formatted = data.map((item, idx) => ({
        key: item.plan_id || idx,
        Plan_Name: item.plan_name,
        Plan_Type: item.plan_type,
        Price: item.plan_price?.toString() || "0",
        Status: item.status,
        plan_id: item.plan_id,
      }));

      setTableData(formatted); // ✅ Also refresh table
    });
  };

  const allColumns = [
    {
      title: "Plan Name",
      dataIndex: "Plan_Name",
      render: (text: string, record: any) => (
        <h6 className="fw-medium">
          <Link to="#">{highlightMatch(text, searchQuery)}</Link>
        </h6>
      ),
      sorter: (a: any, b: any) => a.Plan_Name.length - b.Plan_Name.length,
    },
    {
      title: "Plan Type",
      dataIndex: "Plan_Type",
      render: (text: string) => <>{highlightMatch(text, searchQuery)}</>,
      sorter: (a: any, b: any) => a.Plan_Type.length - b.Plan_Type.length,
    },
    {
    title: "Plan Position",
    dataIndex: "Plan_Position",
    render: (text: string) => <>{text}</>,
  },
  {
    title: "No. of Admins",
    dataIndex: "No_Admin",
    render: (text: string) => <>{text}</>,
  },
  {
    title: "No. of Users",
    dataIndex: "No_User",
    render: (text: string) => <>{text}</>,
  },
    {
      title: "Price",
      dataIndex: "Price",
      render: (text: string) => <>{highlightMatch(text, searchQuery)}</>,
      sorter: (a: any, b: any) => a.Price.length - b.Price.length,
    },
    {
    title: "Currency",
    dataIndex: "Currency",
    render: (text: string) => <>{text}</>,
  },
  {
    title: "Discount Type",
    dataIndex: "Discount_Type",
    render: (text: string) => <>{text}</>,
  },
  {
    title: "Discount %",
    dataIndex: "Discount_Percentage",
    render: (text: string) => <>{text}</>,
  },
  {
    title: "Trial Days",
    dataIndex: "Trial_Days",
    render: (text: string) => <>{text}</>,
  },
    {
      title: "Status",
      dataIndex: "Status",
      render: (text: string, record: any) => (
        <span
          className={`badge ${
            text === "Active" ? "badge-success" : "badge-danger"
          } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {highlightMatch(text, searchQuery)}
        </span>
      ),
      sorter: (a: any, b: any) => a.Status.length - b.Status.length,
    },
    {
    title: "Created Date",
    dataIndex: "Created_Date",
    render: (text: string) => <>{text}</>,
  },
    {
      title: "Action",
      dataIndex: "actions",
      render: (_: any, record: any) => (
        <div className="dropdown table-action">
          <Link
            to="#"
            className="action-icon"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-v" />
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
              <Link
          className="dropdown-item"
          to="#"
          onClick={(e) => {
            e.preventDefault();
            handleEditClick(record.plan_id);
          }}
        >
          <i className="ti ti-edit text-blue" /> Edit
        </Link>
            <Link
              className="dropdown-item"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#delete_invoices"
              onClick={() => setSelectedPlanId(record.plan_id)}
            >
              <i className="ti ti-trash text-danger" /> Delete
            </Link>
          </div>
        </div>
      ),
    },
  ];

  // ✨ Here dynamically filter columns
  const columns = allColumns.filter((col) =>
    visibleColumns.includes(col.title)
  );

  const planName = [
    { value: "Advanced", label: "Advanced" },
    { value: "Basic", label: "Basic" },
    { value: "Enterprise", label: "Enterprise" },
  ];
  const planType = [
    { value: "Monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const planPosition = [
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
  ];

  const discountType = [
    { value: "Fixed", label: "Fixed" },
    { value: "Percentage", label: "Percentage" },
  ];
  const status = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const handleEditClick = (planId: string) => {
    const selected = plans.find((p) => p.plan_id === planId); // Replace with your actual data source
    if (selected) {
      setSelectedPlanId(planId);
      setFormData({
        plan_name: selected.plan_name,
        plan_type: selected.plan_type,
        plan_position: selected.plan_position,
        plan_price: selected.plan_price,
        plan_currency: selected.plan_currency,
        discount_type: selected.discount_type,
        discount_percentage: selected.discount_percentage,
        no_user: selected.no_user,
        no_admin: selected.no_admin,
        trail_days: selected.trail_days,
        status: selected.status,
      });
      setShowEdit(true);
    }
  };

  const openOffcanvas = () => {
    const el = document.getElementById("offcanvas_edit");
    if (el) new (window as any).bootstrap.Offcanvas(el).show();
  };

  useEffect(() => {
    let updated = [...tableData];

    // 🔍 Search Filter
    const query = searchQuery.trim().toLowerCase();
    if (query !== "") {
      updated = updated.filter((plan) => {
        return (
          plan.Plan_Name?.toLowerCase().includes(query) ||
          plan.Plan_Type?.toLowerCase().includes(query) ||
          plan.Status?.toLowerCase().includes(query) ||
          plan.Price?.toString().includes(query)
        );
      });
    }

    // 📅 Date Range Filter
    if (selectedDateRange) {
      const [start, end] = selectedDateRange;
      updated = updated.filter((plan) => {
        if (!plan.Created_Date) return false;
        const created = new Date(plan.Created_Date).getTime();
        return created >= start.getTime() && created <= end.getTime();
      });
    }

    // ↕️ Sorting
    switch (sortOption) {
      case "ascending":
        updated.sort((a, b) => a.Plan_Name.localeCompare(b.Plan_Name));
        break;
      case "descending":
        updated.sort((a, b) => b.Plan_Name.localeCompare(a.Plan_Name));
        break;
      case "recently_added":
        updated.sort(
          (a, b) =>
            new Date(b.Created_Date || 0).getTime() -
            new Date(a.Created_Date || 0).getTime()
        );
        break;
      case "recently_viewed":
        // Add custom logic here if your `plan` has a `viewed_at` field
        break;
      default:
        break;
    }

    setFilteredTableData(updated);
  }, [searchQuery, sortOption, tableData, selectedDateRange]);

  return (
    <>
      <>
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            <div className="row">
              <div className="col-md-12">
                {/* Page Header */}
                <div className="page-header">
                  <div className="row align-items-center">
                    <div className="col-8">
                      <h4 className="page-title"><i className="bi bi-card-list text-primary me-2"/>Packages</h4>
                    </div>
                    <div className="col-4 text-end">
                      <div className="head-icons">
                        <CollapseHeader />
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Page Header */}
                <div className="row">
                  {/* Total Plans */}
                  <div className="col-lg-3 col-md-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center overflow-hidden">
                          <div>
                            <p className="fs-12 fw-medium mb-1 text-truncate">
                              Total Plans
                            </p>
                            <h4>
                              {widgetCounts?.counts.total.total_plan ?? 0}
                            </h4>
                          </div>
                        </div>
                        <div>
                          <span className="avatar avatar-lg bg-primary flex-shrink-0">
                            <i className="ti ti-box fs-16" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Total Plans */}
                  {/* Total Plans */}
                  <div className="col-lg-3 col-md-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center overflow-hidden">
                          <div>
                            <p className="fs-12 fw-medium mb-1 text-truncate">
                              Active Plans
                            </p>
                            <h4>
                              {widgetCounts?.counts.active.active_plan ?? 0}
                            </h4>
                          </div>
                        </div>
                        <div>
                          <span className="avatar avatar-lg bg-success flex-shrink-0">
                            <i className="ti ti-activity-heartbeat fs-16" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Total Plans */}
                  {/* Inactive Plans */}
                  <div className="col-lg-3 col-md-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center overflow-hidden">
                          <div>
                            <p className="fs-12 fw-medium mb-1 text-truncate">
                              Inactive Plans
                            </p>
                            <h4>
                              {widgetCounts?.counts.inactive.inactive_plan ?? 0}
                            </h4>
                          </div>
                        </div>
                        <div>
                          <span className="avatar avatar-lg bg-danger flex-shrink-0">
                            <i className="ti ti-player-pause fs-16" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Inactive Companies */}
                  {/* No of Plans  */}
                  <div className="col-lg-3 col-md-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center overflow-hidden">
                          <div>
                            <p className="fs-12 fw-medium mb-1 text-truncate">
                              No of Plan Types
                            </p>
                            <h4>
                              {widgetCounts?.counts["plan type"]
                                .total_plan_type ?? 0}
                            </h4>
                          </div>
                        </div>
                        <div>
                          <span className="avatar avatar-lg bg-skyblue flex-shrink-0">
                            <i className="ti ti-mask fs-16" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /No of Plans */}
                </div>
                <div className="card">
                  <div className="card-header">
                    {/* Search */}
                    <div className="row align-items-center">
                      <div className="col-sm-4">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            <i className="ti ti-search" />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search Plans"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                          <div className="dropdown me-2">
                            <Link
                              to="#"
                              className="dropdown-toggle"
                              data-bs-toggle="dropdown"
                            >
                              <i className="ti ti-package-export me-2" />
                              Export
                            </Link>
                            <div className="dropdown-menu  dropdown-menu-end">
                              <ul>
                                <li>
                                  <Link to="#" className="dropdown-item">
                                    <i className="ti ti-file-type-pdf text-danger me-1" />
                                    Export as PDF
                                  </Link>
                                </li>
                                <li>
                                  <Link to="#" className="dropdown-item">
                                    <i className="ti ti-file-type-xls text-green me-1" />
                                    Export as Excel{" "}
                                  </Link>
                                </li>
                              </ul>
                            </div>
                          </div>
                          <Link
  to="#"
  className="btn btn-primary"
  onClick={() => setShowAdd(true)} 
>
  <i className="ti ti-square-rounded-plus me-2" />
  Add New Plan
</Link>

                        </div>
                      </div>
                    </div>
                    {/* /Search */}
                  </div>
                  <div className="card-body">
                    {/* Filter */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                      <div className="d-flex align-items-center flex-wrap row-gap-2">
                        <div className="dropdown me-2">
                          <Link
                            to="#"
                            className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                          >
                            <i className="ti ti-sort-ascending-2 me-2" />
                            Sort
                          </Link>
                          <div className="dropdown-menu dropdown-menu-start">
                            <ul>
                              {[
                                { label: "Ascending", value: "ascending" },
                                { label: "Descending", value: "descending" },
                                {
                                  label: "Recently Added",
                                  value: "recently_added",
                                },
                                {
                                  label: "Recently Viewed",
                                  value: "recently_viewed",
                                },
                              ].map(({ label, value }) => {
                                const isActive = sortOption === value;
                                return (
                                  <li key={value}>
                                    <Link
                                      to="#"
                                      className={`dropdown-item d-flex justify-content-between align-items-center ${
                                        isActive ? "fw-bold text-primary" : ""
                                      }`}
                                      onClick={() => setSortOption(value)}
                                    >
                                      <span>
                                        <i className="ti ti-circle-chevron-right me-1" />
                                        {label}
                                      </span>
                                      {isActive && (
                                        <i className="ti ti-check" />
                                      )}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>

                        <div className="icon-form">
                          <span className="form-icon">
                            <i className="ti ti-calendar" />
                          </span>
                          <DateRangePicker
                            initialSettings={initialSettings}
                            onCallback={(start, end, label) => {
                              setSelectedDateRange([
                                start.toDate(),
                                end.toDate(),
                              ]);
                              if (label) setActiveRangeLabel(label);
                            }}
                          >
                            <input
                              className="form-control bookingrange"
                              type="text"
                              readOnly
                            />
                          </DateRangePicker>
                        </div>
                      </div>
                      <div className="d-flex align-items-center flex-wrap row-gap-2">
                        <div className="dropdown me-2">
                          <Link
                            to="#"
                            className="btn bg-soft-purple text-purple"
                            data-bs-toggle="dropdown"
                            data-bs-auto-close="outside"
                          >
                            <i className="ti ti-columns-3 me-2" />
                            Manage Columns
                          </Link>
                          <div className="dropdown-menu  dropdown-menu-md-end dropdown-md p-3">
                            <h4 className="mb-2 fw-semibold">
                              Want to manage datatables?
                            </h4>
                            <p className="mb-3">
                              Please drag and drop your column to reorder your
                              table and enable see option as you want.
                            </p>
                            <div className="border-top pt-3">
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <p className="mb-0 d-flex align-items-center">
                                  <i className="ti ti-grip-vertical me-2" />
                                  Plan Name
                                </p>
                                <div className="status-toggle">
                                  <input
                                    type="checkbox"
                                    id="col-plan-name"
                                    className="check"
                                    checked={visibleColumns.includes(
                                      "Plan Name"
                                    )}
                                    onChange={() =>
                                      handleToggleColumn("Plan Name")
                                    }
                                  />
                                  <label
                                    htmlFor="col-plan-name"
                                    className="checktoggle"
                                  />
                                </div>
                              </div>
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <p className="mb-0 d-flex align-items-center">
                                  <i className="ti ti-grip-vertical me-2" />
                                  Plan Type
                                </p>
                                <div className="status-toggle">
                                  <input
                                    type="checkbox"
                                    id="col-plan-type"
                                    className="check"
                                    checked={visibleColumns.includes(
                                      "Plan Type"
                                    )}
                                    onChange={() =>
                                      handleToggleColumn("Plan Type")
                                    }
                                  />
                                  <label
                                    htmlFor="col-plan-type"
                                    className="checktoggle"
                                  />
                                </div>
                              </div>
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <p className="mb-0 d-flex align-items-center">
                                  <i className="ti ti-grip-vertical me-2" />
                                  Price
                                </p>
                                <div className="status-toggle">
                                  <input
                                    type="checkbox"
                                    id="col-price"
                                    className="check"
                                    checked={visibleColumns.includes("Price")}
                                    onChange={() => handleToggleColumn("Price")}
                                  />
                                  <label
                                    htmlFor="col-price"
                                    className="checktoggle"
                                  />
                                </div>
                              </div>
                              {/* <div className="d-flex align-items-center justify-content-between mb-3">
                                <p className="mb-0 d-flex align-items-center">
                                  <i className="ti ti-grip-vertical me-2" />
                                  Created Date
                                </p>
                                <div className="status-toggle">
                                  <input
                                    type="checkbox"
                                    id="col-date"
                                    className="check"
                                  />
                                  <label
                                    htmlFor="col-date"
                                    className="checktoggle"
                                  />
                                </div>
                              </div> */}
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <p className="mb-0 d-flex align-items-center">
                                  <i className="ti ti-grip-vertical me-2" />
                                  Status
                                </p>
                                <div className="status-toggle">
                                  <input
                                    type="checkbox"
                                    id="col-status"
                                    className="check"
                                    checked={visibleColumns.includes("Status")}
                                    onChange={() =>
                                      handleToggleColumn("Status")
                                    }
                                  />
                                  <label
                                    htmlFor="col-status"
                                    className="checktoggle"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="form-sorts dropdown me-2">
                          <Link
                            to="#"
                            data-bs-toggle="dropdown"
                            data-bs-auto-close="outside"
                          >
                            <i className="ti ti-filter-share" />
                            Filter
                          </Link>
                          <div className="filter-dropdown-menu dropdown-menu  dropdown-menu-md-end p-3">
                            <div className="filter-set-view">
                              <div className="filter-set-head">
                                <h4>
                                  <i className="ti ti-filter-share" />
                                  Filter
                                </h4>
                              </div>
                              <div className="accordion" id="accordionExample">
                                <div className="filter-set-content">
                                  <div className="filter-set-content-head">
                                    <Link
                                      to="#"
                                      className="collapsed"
                                      data-bs-toggle="collapse"
                                      data-bs-target="#location"
                                      aria-expanded="false"
                                      aria-controls="location"
                                    >
                                      Select Plan
                                    </Link>
                                  </div>
                                  <div
                                    className="filter-set-contents accordion-collapse collapse"
                                    id="location"
                                    data-bs-parent="#accordionExample"
                                  >
                                    <div className="filter-content-list">
                                      <ul>
                                        <li>
                                          <div className="filter-checks">
                                            <label className="checkboxs">
                                              <input
                                                type="checkbox"
                                                defaultChecked
                                              />
                                              <span className="checkmarks" />
                                              Monthly
                                            </label>
                                          </div>
                                        </li>
                                        <li>
                                          <div className="filter-checks">
                                            <label className="checkboxs">
                                              <input
                                                type="checkbox"
                                                defaultChecked
                                              />
                                              <span className="checkmarks" />
                                              Yearly
                                            </label>
                                          </div>
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                                <div className="filter-set-content">
                                  <div className="filter-set-content-head">
                                    <Link
                                      to="#"
                                      className="collapsed"
                                      data-bs-toggle="collapse"
                                      data-bs-target="#Status"
                                      aria-expanded="false"
                                      aria-controls="Status"
                                    >
                                      Status
                                    </Link>
                                  </div>
                                  <div
                                    className="filter-set-contents accordion-collapse collapse"
                                    id="Status"
                                    data-bs-parent="#accordionExample"
                                  >
                                    <div className="filter-content-list">
                                      <ul>
                                        <li>
                                          <div className="filter-checks">
                                            <label className="checkboxs">
                                              <input
                                                type="checkbox"
                                                defaultChecked
                                              />
                                              <span className="checkmarks" />
                                              Active
                                            </label>
                                          </div>
                                        </li>
                                        <li>
                                          <div className="filter-checks">
                                            <label className="checkboxs">
                                              <input
                                                type="checkbox"
                                                defaultChecked
                                              />
                                              <span className="checkmarks" />
                                              Inactive
                                            </label>
                                          </div>
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="filter-reset-btns">
                                <div className="row">
                                  <div className="col-6">
                                    <Link to="#" className="btn btn-light">
                                      Reset
                                    </Link>
                                  </div>
                                  <div className="col-6">
                                    <Link
                                      to={all_routes.superadminPackagelist}
                                      className="btn btn-primary"
                                    >
                                      Filter
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="view-icons">
                          <Link
                            to={all_routes.superadminPackagelist}
                            className="active"
                          >
                            <i className="ti ti-list-tree" />
                          </Link>
                          <Link to={all_routes.superadminPackageGrid}>
                            <i className="ti ti-grid-dots" />
                          </Link>
                        </div>
                      </div>
                    </div>
                    {/* /Filter */}
                    {/* Manage Users List */}
                    <div className="table-responsive custom-table">
                      {/* <Table dataSource={tableData} columns={columns} /> */}
                      <Table
                        columns={columns}
                        dataSource={filteredTableData}
                        className="custom-table"
                        loading={loading}
                        pagination={{
                          current: currentPage,
                          pageSize: pageSize,
                          showSizeChanger: true,
                          pageSizeOptions: ["10", "20", "50"],
                          onChange: (page: number, size: number) => {
                            setCurrentPage(page);
                            setPageSize(size);
                          },
                        }}
                        scroll={{ x: "max-content" }} // Enables horizontal scroll
                        components={{
                          header: {
                            cell: (props: any) => (
                              <th
                                {...props}
                                style={{
                                  ...props.style,
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 2, // Better layering over scrollbars/fixed columns
                                  background: "#fff",
                                  boxShadow: "inset 0 -1px 0 #f50e0e", // ✅ fix this invalid color
                                }}
                              />
                            ),
                          },
                        }}
                      />
                    </div>
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <div className="datatable-length" />
                      </div>
                      <div className="col-md-6">
                        <div className="datatable-paginate" />
                      </div>
                    </div>
                    {/* /Manage Users List */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Page Wrapper */}
      </>
      <>
        {/* Add Plan */}
{showAdd && (
  <>
    {/* Backdrop */}
    <div
      className="offcanvas-backdrop fade show"
      onClick={() => setShowAdd(false)}
      style={{ zIndex: 1040 }}
    />

    {/* Panel */}
    <div
      className="offcanvas offcanvas-end offcanvas-large show d-block"
      style={{ visibility: "visible", zIndex: 1050 }}
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">Add New Plan</h5>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              onClick={() => setShowAdd(false)}
          aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="offcanvas-body">
            <form>
              <h6 className="mt-3 mb-3 border-bottom pb-2 fw-semibold">
                <i className="ti ti-file-dollar me-2 text-primary" />
                Plan Information
              </h6>
              <div className="row g-3">
                {/* Plan Name */}
                <div className="col-md-6">
                  <label className="form-label">
                    Plan Name<span className="text-danger"> *</span>
                  </label>
                  <Select
                    options={planName}
                    value={planName.find(
                      (opt) => opt.value === formData.plan_name
                    )}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        plan_name: selected?.value || "",
                      })
                    }
                  />
                </div>

                {/* Plan Type */}
                <div className="col-md-6">
                  <label className="form-label">
                    Plan Type<span className="text-danger"> *</span>
                  </label>
                  <Select
                    options={planType}
                    value={planType.find(
                      (opt) => opt.value === formData.plan_type
                    )}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        plan_type: selected?.value || "",
                      })
                    }
                  />
                </div>

                {/* Plan Position */}
                <div className="col-md-6">
                  <label className="form-label">
                    Plan Position<span className="text-danger"> *</span>
                  </label>
                  <Select
                    options={planPosition}
                    value={planPosition.find(
                      (opt) => opt.value === formData.plan_position
                    )}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        plan_position: selected?.value || "",
                      })
                    }
                  />
                </div>

                {/* Plan Price */}
                <div className="col-md-6">
                  <label className="form-label">Plan Price</label>
                  <input
                    type="number"
                    className="form-control"
                    inputMode="numeric"
                    value={formData.plan_price}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        plan_price: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>

                {/* Plan Currency */}
                <div className="col-md-6">
                  <label className="form-label">
                    Plan Currency<span className="text-danger"> *</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.plan_currency}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        plan_currency: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Discount Type */}
                <div className="col-md-3">
                  <label className="form-label">
                    Discount Type<span className="text-danger"> *</span>
                  </label>
                  <Select
                    options={discountType}
                    value={discountType.find(
                      (opt) => opt.value === formData.discount_type
                    )}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        discount_type: selected?.value || "",
                      })
                    }
                  />
                </div>

                {/* Discount % */}
                <div className="col-md-3">
                  <label className="form-label">Discount %</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.discount_percentage}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        discount_percentage: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Number of Users */}
                <div className="col-md-6">
                  <label className="form-label">Number of Users</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.no_user}
                    onChange={(e) =>
                      setFormData({ ...formData, no_user: e.target.value })
                    }
                  />
                </div>

                {/* Number of Admins */}
                <div className="col-md-6">
                  <label className="form-label">Number of Admins</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.no_admin}
                    onChange={(e) =>
                      setFormData({ ...formData, no_admin: e.target.value })
                    }
                  />
                </div>

                {/* Trial Days */}
                <div className="col-md-6">
                  <label className="form-label">Trial Days</label>
                  <input
                    type="number"
                    className="form-control"
                    inputMode="numeric"
                    
                    value={formData.trail_days}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        trail_days: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>

                {/* Status (Radio) */}
                <div className="col-md-6">
                  <label className="form-label d-block">Status</label>
                  <div className="d-flex mt-2">
                    <div className="form-check me-3">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="status"
                        id="statusActive"
                        value="Active"
                        checked={formData.status === "Active"}
                        onChange={(e) =>
                          setFormData({ ...formData, status: e.target.value })
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="statusActive"
                      >
                        Active
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="status"
                        id="statusInactive"
                        value="Inactive"
                        checked={formData.status === "Inactive"}
                        onChange={(e) =>
                          setFormData({ ...formData, status: e.target.value })
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="statusInactive"
                      >
                        Inactive
                      </label>
                    </div>
                  </div>
                </div>
              </div>

             {/* Buttons */}
          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={() => setShowAdd(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                handleCreatePlan();
                setShowAdd(false); // ✅ close after create
              }}
            >
              Create
            </button>
              </div>
            </form>
          </div>
        </div>
        </>
)}
        {/* /Add Plan */}
        {/* Edit Plan */}
        {showEdit && (
        <>
          {/* Backdrop */}
          <div
            className="offcanvas-backdrop fade show"
            onClick={() => setShowEdit(false)}
            style={{ zIndex: 1040 }}
          />

          {/* Offcanvas Panel */}
          <div
            className="offcanvas offcanvas-end offcanvas-large show d-block"
            style={{ visibility: "visible", zIndex: 1050 }}
          >
            <div className="offcanvas-header border-bottom">
              <h5 className="fw-semibold">Edit Plan</h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                onClick={() => setShowEdit(false)}
                aria-label="Close"
              >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="offcanvas-body">
            <form>
              {/* Section header */}
              <h6 className="mt-1 mb-4 border-bottom pb-2 fw-semibold">
                <i className="ti ti-file-dollar me-2 text-primary" />
                Plan Information
              </h6>

              <div className="row g-3">
                {/* Plan Name */}
                <div className="col-md-6">
                  <label className="form-label">
                    Plan Name<span className="text-danger"> *</span>
                  </label>
                  <Select
                    options={planName}
                    value={planName.find(
                      (opt) => opt.value === formData.plan_name
                    )}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        plan_name: selected?.value || "",
                      })
                    }
                  />
                </div>

                {/* Plan Type */}
                <div className="col-md-6">
                  <label className="form-label">
                    Plan Type<span className="text-danger"> *</span>
                  </label>
                  <Select
                    options={planType}
                    value={planType.find(
                      (opt) => opt.value === formData.plan_type
                    )}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        plan_type: selected?.value || "",
                      })
                    }
                  />
                </div>

                {/* Plan Position */}
                <div className="col-md-6">
                  <label className="form-label">
                    Plan Position<span className="text-danger"> *</span>
                  </label>
                  <Select
                    options={planPosition}
                    value={planPosition.find(
                      (opt) => opt.value === formData.plan_position
                    )}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        plan_position: selected?.value || "",
                      })
                    }
                  />
                </div>

                {/* Plan Price */}
                <div className="col-md-6">
                  <label className="form-label">Plan Price</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="form-control"
                    value={formData.plan_price}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        plan_price: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>

                {/* Plan Currency */}
                <div className="col-md-6">
                  <label className="form-label">
                    Plan Currency<span className="text-danger"> *</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.plan_currency}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        plan_currency: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Discount Type */}
                <div className="col-md-3">
                  <label className="form-label">
                    Discount Type<span className="text-danger"> *</span>
                  </label>
                  <Select
                    options={discountType}
                    value={discountType.find(
                      (opt) => opt.value === formData.discount_type
                    )}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        discount_type: selected?.value || "",
                      })
                    }
                  />
                </div>

                {/* Discount % */}
                <div className="col-md-3">
                  <label className="form-label">
                    Discount<span className="text-danger"> *</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.discount_percentage}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        discount_percentage: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Number of Users */}
                <div className="col-md-6">
                  <label className="form-label">Number of Users</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.no_user}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        no_user: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Number of Admins */}
                <div className="col-md-6">
                  <label className="form-label">Number of Admins</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.no_admin}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        no_admin: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Trial Days */}
                <div className="col-md-6">
                  <label className="form-label">Trial Days</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="form-control"
                    value={formData.trail_days}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        trail_days: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>

                {/* Status (Radio) */}
                <div className="col-md-6">
                  <label className="form-label d-block">
                    Status<span className="text-danger"> *</span>
                  </label>
                  <div className="d-flex mt-1">
                    <div className="form-check me-3">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="statusActive"
                        name="status"
                        value="Active"
                        checked={formData.status === "Active"}
                        onChange={(e) =>
                          setFormData({ ...formData, status: e.target.value })
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="statusActive"
                      >
                        Active
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="statusInactive"
                        name="status"
                        value="Inactive"
                        checked={formData.status === "Inactive"}
                        onChange={(e) =>
                          setFormData({ ...formData, status: e.target.value })
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="statusInactive"
                      >
                        Inactive
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex align-items-center justify-content-end mt-4">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={() => setShowEdit(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleUpdatePlan(selectedPlanId!)}
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
        </>
        )}

        {/* /Edit Plan */}
        {/* Delete Company */}
        <div className="modal fade" id="delete_invoices" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <div className="text-center">
                  <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                    <i className="ti ti-trash-x fs-36 text-danger" />
                  </div>
                  <h4 className="mb-2">Remove Plan?</h4>
                  <p className="mb-0">
                    Are you sure you want to remove <br /> company you selected.
                  </p>
                  <div className="d-flex align-items-center justify-content-center mt-4">
                    <Link
                      to="#"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      data-bs-dismiss="modal"
                      className="btn btn-danger"
                      onClick={() => handleDeletePlan(selectedPlanId!)}
                    >
                      Yes, Delete it
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Delete Company */}
      </>
    </>
  );
};

export default Packages;
